"use client";

/**
 * Ends the page-change wait (lib/nav-pending) once the new route has
 * rendered: the pathname or the query changed. Back and forward to another
 * page start one too, since no link click announces them.
 */
import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { endNavigation, startNavigation } from "@/lib/nav-pending";

function decoded(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

export default function NavigationWatcher() {
  const pathname = usePathname();
  const search = useSearchParams();
  const query = search?.toString() ?? "";
  const key = `${pathname}?${query}`;
  const shown = useRef({ path: pathname, query });

  useEffect(() => {
    shown.current = { path: pathname, query };
    endNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    // Only a back that lands on another URL starts the wait. Closing a sheet,
    // the drawer or the lightbox pops their own history entry with the URL
    // unchanged (lib/use-back-to-close): nothing re-renders to end the wait,
    // so the mark turned for the full 15 s, and on a phone on /map that
    // animation competed with the map (owner, 2026-10-19).
    const onPop = () => {
      const samePath = decoded(window.location.pathname) === decoded(shown.current.path ?? "");
      const sameQuery = new URLSearchParams(window.location.search).toString() === shown.current.query;
      if (!(samePath && sameQuery)) startNavigation();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return null;
}
